import React, { useState, useEffect } from 'react'
import { infographicsService, formatInfographicDate } from '../services/infographicsService'
import { Eye, Heart, Download, Calendar, User, Search, Image as ImageIcon, ZoomIn, X, Plus, Edit, Trash2, Upload, FileImage } from 'lucide-react'
import { useUser } from '../context/UserContext'

const Infographics = ({ searchTerm, selectedCategory, selectedDifficulty, userRole = 'user' }) => {
  const { canCreate, canEdit, canDelete } = useUser()
  const [infographics, setInfographics] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [selectedInfographic, setSelectedInfographic] = useState(null)
  const [showModal, setShowModal] = useState(false)
  const [likedInfographics, setLikedInfographics] = useState(new Set())
  const [localSearchTerm, setLocalSearchTerm] = useState('')
  const [sortBy, setSortBy] = useState('published_at')
  const [sortOrder, setSortOrder] = useState('desc')
  const [featuredInfographics, setFeaturedInfographics] = useState([])
  const [showFeatured, setShowFeatured] = useState(true)
  const [imageLoading, setImageLoading] = useState({})
  const [imageErrors, setImageErrors] = useState({})
  
  // CRUD state
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [editingInfographic, setEditingInfographic] = useState(null)
  const [infographicForm, setInfographicForm] = useState({
    title: '',
    description: '',
    category: '',
    difficulty: 'beginner',
    tags: [],
    status: 'draft',
    featured: false,
    altText: ''
  })
  const [images, setImages] = useState([])
  const [uploadingImages, setUploadingImages] = useState(false)
  const [formErrors, setFormErrors] = useState({})
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(9)
  const [totalPages, setTotalPages] = useState(0)

  // Load infographics on component mount and when filters change
  useEffect(() => {
    loadInfographics()
  }, [searchTerm, selectedCategory, selectedDifficulty, sortBy, sortOrder])

  // Load featured infographics
  useEffect(() => {
    loadFeaturedInfographics()
  }, [])

  const loadInfographics = async () => {
    try {
      setLoading(true)
      setError(null)
      
      const filters = {
        search: searchTerm || localSearchTerm,
        category: selectedCategory,
        difficulty: selectedDifficulty,
        sortBy,
        sortOrder
      }
      
      // Load all infographics regardless of status
      const data = await infographicsService.getAllInfographics()
      
      // Apply client-side filtering since getUserInfographics doesn't support server-side filters
      let filteredData = data || []
      
      if (filters.search) {
        filteredData = filteredData.filter(infographic => 
          infographic.title?.toLowerCase().includes(filters.search.toLowerCase()) ||
          infographic.description?.toLowerCase().includes(filters.search.toLowerCase())
        )
      }
      
      if (filters.category && filters.category !== 'all') {
        filteredData = filteredData.filter(infographic => infographic.category === filters.category)
      }
      
      if (filters.difficulty && filters.difficulty !== 'all') {
        filteredData = filteredData.filter(infographic => infographic.difficulty === filters.difficulty)
      }
      
      // Apply sorting
      if (filters.sortBy) {
        filteredData.sort((a, b) => {
          let aValue = a[filters.sortBy]
          let bValue = b[filters.sortBy]
          
          if (filters.sortBy === 'created_at' || filters.sortBy === 'updated_at' || filters.sortBy === 'published_at') {
            aValue = new Date(aValue)
            bValue = new Date(bValue)
          }
          
          if (filters.sortOrder === 'desc') {
            return bValue > aValue ? 1 : -1
          } else {
            return aValue > bValue ? 1 : -1
          }
        })
      }
      
      setInfographics(filteredData)
      
      // Optimize: Load user likes in batch if infographics exist
      if (data.length > 0) {
        try {
          const infographicIds = data.map(infographic => infographic.id)
          const userLikes = await infographicsService.getUserLikes(infographicIds)
          setLikedInfographics(new Set(userLikes))
        } catch (likesErr) {
          console.warn('Failed to load user likes:', likesErr)
          setLikedInfographics(new Set())
        }
      } else {
        setLikedInfographics(new Set())
      }
      
    } catch (err) {
      console.error('Error loading infographics:', err)
      setError('Failed to load infographics. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const loadFeaturedInfographics = async () => {
    try {
      const featured = await infographicsService.getFeaturedInfographics(3)
      setFeaturedInfographics(featured)
    } catch (err) {
      console.error('Error loading featured infographics:', err)
    }
  }

  const handleInfographicClick = async (infographic) => {
    setSelectedInfographic(infographic)
    setShowModal(true)
    
    // Record view
    try {
      await infographicsService.recordView(infographic.id)
      // Update view count in local state
      setInfographics(prev => prev.map(i => 
        i.id === infographic.id ? { ...i, views: (i.views || 0) + 1 } : i
      ))
    } catch (err) {
      console.warn('Failed to record infographic view:', err)
    }
  }

  const handleLikeToggle = async (infographicId, event) => {
    event.stopPropagation()
    
    try {
      const result = await infographicsService.toggleLike(infographicId)
      
      // Update local state
      const newLikedInfographics = new Set(likedInfographics)
      if (result.liked) {
        newLikedInfographics.add(infographicId)
      } else {
        newLikedInfographics.delete(infographicId)
      }
      setLikedInfographics(newLikedInfographics)
      
      // Update like count in infographics list
      setInfographics(prev => prev.map(infographic => {
        if (infographic.id === infographicId) {
          return {
            ...infographic,
            likes: result.liked ? (infographic.likes || 0) + 1 : Math.max(0, (infographic.likes || 0) - 1)
          }
        }
        return infographic
      }))
      
    } catch (err) {
      console.error('Error toggling like:', err)
    }
  }

  const handleDownload = async (infographic, event) => {
    event.stopPropagation()
    
    try {
      // Record download
      await infographicsService.recordDownload(infographic.id)
      
      // Create download link
      const link = document.createElement('a')
      link.href = infographic.imageUrl
      link.download = `${infographic.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.${infographic.fileType?.split('/')[1] || 'png'}`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      
      // Update download count in local state
      setInfographics(prev => prev.map(i => 
        i.id === infographic.id ? { ...i, downloads: (i.downloads || 0) + 1 } : i
      ))
      
    } catch (err) {
      console.error('Error downloading infographic:', err)
    }
  }

  const handleLocalSearch = (e) => {
    e.preventDefault()
    loadInfographics()
  }

  const handleImageLoad = (infographicId) => {
    setImageLoading(prev => ({ ...prev, [infographicId]: false }))
  }

  const handleImageError = (infographicId) => {
    setImageLoading(prev => ({ ...prev, [infographicId]: false }))
    setImageErrors(prev => ({ ...prev, [infographicId]: true }))
  }

  const closeModal = () => {
    setShowModal(false)
    setSelectedInfographic(null)
  }

  // CRUD Functions
  const handleCreateInfographic = () => {
    setInfographicForm({
      title: '',
      description: '',
      category: '',
      difficulty: 'beginner',
      tags: [],
      status: 'draft',
      featured: false,
      altText: ''
    })
    setImages([])
    setFormErrors({})
    setShowCreateModal(true)
  }

  const handleEditInfographic = (infographic) => {
    setEditingInfographic(infographic)
    setInfographicForm({
      title: infographic.title || '',
      description: infographic.description || '',
      category: infographic.category || '',
      difficulty: infographic.difficulty || 'beginner',
      tags: infographic.tags || [],
      status: infographic.status || 'draft',
      featured: infographic.featured || false,
      altText: infographic.alt_text || ''
    })
    setImages([])
    setFormErrors({})
    setShowEditModal(true)
  }

  const handleDeleteInfographic = async (infographicId) => {
    if (!window.confirm('Are you sure you want to delete this infographic?')) {
      return
    }

    try {
      await infographicsService.deleteInfographic(infographicId)
      setInfographics(prev => prev.filter(inf => inf.id !== infographicId))
      setFeaturedInfographics(prev => prev.filter(inf => inf.id !== infographicId))
    } catch (error) {
      console.error('Error deleting infographic:', error)
      alert('Failed to delete infographic')
    }
  }

  const handleFormSubmit = async (e) => {
    e.preventDefault()
    setFormErrors({})

    // Validate form
    const errors = {}
    if (!infographicForm.title.trim()) errors.title = 'Title is required'
    if (!infographicForm.description.trim()) errors.description = 'Description is required'
    if (!infographicForm.category.trim()) errors.category = 'Category is required'
    if (images.length === 0 && !editingInfographic) errors.images = 'At least one image is required'

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors)
      return
    }

    try {
      let infographicData = {
        ...infographicForm,
        tags: Array.isArray(infographicForm.tags) ? infographicForm.tags : 
              infographicForm.tags.split(',').map(tag => tag.trim()).filter(tag => tag)
      }

      if (editingInfographic) {
        // Update existing infographic
        const imageFile = images.length > 0 ? images[0] : null
        const updatedInfographic = await infographicsService.updateInfographic(
          editingInfographic.id,
          infographicData,
          imageFile
        )
        
        // Upload additional images if any (skip first one as it's handled by updateInfographic)
        if (images.length > 1) {
          await infographicsService.uploadInfographicImages(editingInfographic.id, images.slice(1))
        }
        
        setInfographics(prev => prev.map(inf => 
          inf.id === editingInfographic.id ? { ...inf, ...updatedInfographic } : inf
        ))
        setShowEditModal(false)
      } else {
        // Create new infographic
        const imageFile = images.length > 0 ? images[0] : null
        const newInfographic = await infographicsService.createInfographic(infographicData, imageFile)
        
        // Upload additional images if any (skip first one as it's already uploaded)
        if (images.length > 1) {
          await infographicsService.uploadInfographicImages(newInfographic.id, images.slice(1))
        }
        
        setInfographics(prev => [newInfographic, ...prev])
        setShowCreateModal(false)
      }
      
      setEditingInfographic(null)
      setImages([])
    } catch (error) {
      console.error('Error saving infographic:', error)
      setFormErrors({ submit: 'Failed to save infographic' })
    }
  }

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files)
    if (files.length === 0) return

    // Validate files
    const validFiles = files.filter(file => {
      if (!file.type.startsWith('image/')) {
        alert(`${file.name} is not an image file`)
        return false
      }
      if (file.size > 10 * 1024 * 1024) { // 10MB limit
        alert(`${file.name} is too large. Maximum size is 10MB`)
        return false
      }
      return true
    })

    setImages(prev => [...prev, ...validFiles])
  }

  const handleRemoveImage = (index) => {
    setImages(prev => prev.filter((_, i) => i !== index))
  }

  const closeCreateModal = () => {
    setShowCreateModal(false)
    setInfographicForm({
      title: '',
      description: '',
      category: '',
      difficulty: 'beginner',
      tags: [],
      status: 'draft',
      featured: false,
      altText: ''
    })
    setImages([])
    setFormErrors({})
  }

  const closeEditModal = () => {
    setShowEditModal(false)
    setEditingInfographic(null)
    setInfographicForm({
      title: '',
      description: '',
      category: '',
      difficulty: 'beginner',
      tags: [],
      status: 'draft',
      featured: false,
      altText: ''
    })
    setImages([])
    setFormErrors({})
  }

  // Pagination logic
  const totalItems = infographics.length
  const calculatedTotalPages = Math.ceil(totalItems / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const paginatedInfographics = infographics.slice(startIndex, endIndex)

  // Update total pages when infographics change
  useEffect(() => {
    setTotalPages(calculatedTotalPages)
    if (currentPage > calculatedTotalPages && calculatedTotalPages > 0) {
      setCurrentPage(1)
    }
  }, [infographics.length, itemsPerPage, calculatedTotalPages, currentPage])

  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1)
  }, [searchTerm, selectedCategory, localSearchTerm, sortBy, sortOrder])

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-gray-600">Loading infographics...</span>
      </div>
    )
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="text-red-600 mb-4">{error}</div>
        <button 
          onClick={loadInfographics}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          Try Again
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header with Create Button */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Infographics</h1>
        {canCreate() && (
          <button
            onClick={handleCreateInfographic}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Create Infographic
          </button>
        )}
      </div>

      {/* Search and Filter Controls */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Local Search */}
          <form onSubmit={handleLocalSearch} className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search infographics..."
                value={localSearchTerm}
                onChange={(e) => setLocalSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </form>
          
          {/* Sort Controls */}
          <div className="flex gap-2">
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="published_at">Date Published</option>
              <option value="views">Most Viewed</option>
              <option value="likes">Most Liked</option>
              <option value="downloads">Most Downloaded</option>
              <option value="title">Title</option>
            </select>
            
            <select
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="desc">Descending</option>
              <option value="asc">Ascending</option>
            </select>
          </div>
        </div>
      </div>

      {/* Featured Infographics */}
      {showFeatured && featuredInfographics.length > 0 && (
        <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900 flex items-center">
              <ImageIcon className="w-5 h-5 mr-2 text-purple-600" />
              Featured Infographics
            </h2>
            <button
              onClick={() => setShowFeatured(false)}
              className="text-gray-500 hover:text-gray-700"
            >
              ×
            </button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {featuredInfographics.map((infographic) => (
              <div
                key={infographic.id}
                onClick={() => handleInfographicClick(infographic)}
                className="bg-white rounded-lg overflow-hidden cursor-pointer hover:shadow-md transition-shadow border border-purple-100"
              >
                <div className="aspect-video bg-gray-100 relative">
                  {imageErrors[infographic.id] ? (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <ImageIcon className="w-8 h-8 text-gray-400" />
                      <span className="ml-2 text-sm text-gray-500">Image not available</span>
                    </div>
                  ) : (
                    <img
                      src={infographic.thumbnailUrl || infographic.imageUrl}
                      alt={infographic.altText || infographic.title}
                      className="w-full h-full object-cover"
                      onLoad={() => handleImageLoad(infographic.id)}
                      onError={() => handleImageError(infographic.id)}
                    />
                  )}
                  {imageLoading[infographic.id] && (
                    <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600"></div>
                    </div>
                  )}
                </div>
                <div className="p-4">
                  <h3 className="font-medium text-gray-900 mb-2 line-clamp-2">{infographic.title}</h3>
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <span className="flex items-center">
                      <Eye className="w-3 h-3 mr-1" />
                      {infographic.views || 0}
                    </span>
                    <span className="flex items-center">
                      <Download className="w-3 h-3 mr-1" />
                      {infographic.downloads || 0}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-8">
          <button
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
            
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }
              
              return (
                <button
                  key={pageNum}
                  onClick={() => setCurrentPage(pageNum)}
                  className={`px-3 py-2 text-sm border rounded-lg ${
                    currentPage === pageNum
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {pageNum}
                </button>
              );
            })}
            
            <button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        )}

      {/* Infographics Grid */}
      {infographics.length === 0 ? (
        <div className="text-center py-12">
          <ImageIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No infographics found</h3>
          <p className="text-gray-600">Try adjusting your search or filter criteria.</p>
        </div>
      ) : (
        <>
          {/* Pagination Info */}
          <div className="flex justify-between items-center mb-4">
            <div className="text-sm text-gray-600">
              Showing {startIndex + 1}-{Math.min(endIndex, totalItems)} of {totalItems} infographics
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600">Items per page:</label>
              <select
                value={itemsPerPage}
                onChange={(e) => setItemsPerPage(Number(e.target.value))}
                className="px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value={6}>6</option>
                <option value={9}>9</option>
                <option value={12}>12</option>
                <option value={16}>16</option>
                <option value={20}>20</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {paginatedInfographics.map((infographic) => (
            <div
              key={infographic.id}
              onClick={() => handleInfographicClick(infographic)}
              className="bg-white rounded-lg shadow-sm border hover:shadow-md transition-shadow cursor-pointer overflow-hidden group"
            >
              {/* Image Container */}
              <div className="aspect-video bg-gray-100 relative overflow-hidden">
                {imageErrors[infographic.id] ? (
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <ImageIcon className="w-8 h-8 text-gray-400 mb-2" />
                    <span className="text-sm text-gray-500">Image not available</span>
                  </div>
                ) : (
                  <>
                    <img
                      src={infographic.thumbnailUrl || infographic.imageUrl}
                      alt={infographic.altText || infographic.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      onLoad={() => handleImageLoad(infographic.id)}
                      onError={() => handleImageError(infographic.id)}
                    />
                    {/* Overlay */}
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-opacity duration-300 flex items-center justify-center">
                      <ZoomIn className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    </div>
                  </>
                )}
                
                {imageLoading[infographic.id] && (
                  <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                  </div>
                )}
                
                {/* Action Buttons Overlay */}
                <div className="absolute top-2 right-2 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <button
                    onClick={(e) => handleLikeToggle(infographic.id, e)}
                    className={`p-2 rounded-full backdrop-blur-sm transition-colors ${
                      likedInfographics.has(infographic.id)
                        ? 'bg-red-500 text-white'
                        : 'bg-white bg-opacity-80 text-gray-700 hover:bg-red-500 hover:text-white'
                    }`}
                  >
                    <Heart className={`w-4 h-4 ${likedInfographics.has(infographic.id) ? 'fill-current' : ''}`} />
                  </button>
                  <button
                    onClick={(e) => handleDownload(infographic, e)}
                    className="p-2 rounded-full bg-white bg-opacity-80 text-gray-700 hover:bg-blue-500 hover:text-white backdrop-blur-sm transition-colors"
                  >
                    <Download className="w-4 h-4" />
                  </button>
                </div>
              </div>
              
              {/* Content */}
              <div className="p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2">
                  {infographic.title}
                </h3>
                
                {/* Category and Difficulty */}
                <div className="flex items-center gap-2 mb-3">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                    {infographic.category}
                  </span>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    infographic.difficulty === 'beginner' ? 'bg-green-100 text-green-800' :
                    infographic.difficulty === 'intermediate' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {infographic.difficulty}
                  </span>
                </div>
                
                {/* Status and File Info */}
                <div className="flex items-center justify-between mb-3">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    infographic.status === 'published' ? 'bg-green-100 text-green-800' :
                    infographic.status === 'draft' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {infographic.status || 'draft'}
                  </span>
                  {infographic.featured && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                      ⭐ Featured
                    </span>
                  )}
                  {infographic.file_size && (
                    <span className="text-xs text-gray-500">
                      {(infographic.file_size / 1024 / 1024).toFixed(1)} MB
                    </span>
                  )}
                </div>
                
                {/* Tags */}
                {infographic.tags && infographic.tags.length > 0 && (
                  <div className="mb-3">
                    <div className="flex flex-wrap gap-1">
                      {infographic.tags.slice(0, 3).map((tag, index) => (
                        <span key={index} className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-700">
                          #{tag}
                        </span>
                      ))}
                      {infographic.tags.length > 3 && (
                        <span className="text-xs text-gray-500">+{infographic.tags.length - 3} more</span>
                      )}
                    </div>
                  </div>
                )}
                
                {/* Description */}
                {infographic.description && (
                  <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                    {infographic.description}
                  </p>
                )}
                
                {/* Stats */}
                <div className="flex items-center justify-between text-sm text-gray-500 mb-3">
                  <div className="flex items-center space-x-3">
                    <span className="flex items-center">
                      <Eye className="w-4 h-4 mr-1" />
                      {infographic.views || 0}
                    </span>
                    <span className="flex items-center">
                      <Heart className="w-4 h-4 mr-1" />
                      {infographic.likes || 0}
                    </span>
                    <span className="flex items-center">
                      <Download className="w-4 h-4 mr-1" />
                      {infographic.downloads || 0}
                    </span>
                  </div>
                </div>
                
                {/* Author and Dates */}
                <div className="pt-3 border-t border-gray-100 space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <User className="w-4 h-4 mr-2 text-gray-400" />
                      <span className="text-sm text-gray-600">{infographic.authorName}</span>
                    </div>
                    {infographic.file_type && (
                      <span className="text-xs text-gray-500 uppercase">
                        {infographic.file_type.replace('image/', '')}
                      </span>
                    )}
                  </div>
                  <div className="grid grid-cols-1 gap-1 text-xs text-gray-500">
                    <div className="flex items-center">
                      <Calendar className="w-3 h-3 mr-1" />
                      <span>Created: {formatInfographicDate(infographic.created_at)}</span>
                    </div>
                    {infographic.publishedAt && (
                      <div className="flex items-center">
                        <Calendar className="w-3 h-3 mr-1" />
                        <span>Published: {formatInfographicDate(infographic.publishedAt)}</span>
                      </div>
                    )}
                    {infographic.updated_at && infographic.updated_at !== infographic.created_at && (
                      <div className="flex items-center">
                        <Calendar className="w-3 h-3 mr-1" />
                        <span>Updated: {formatInfographicDate(infographic.updated_at)}</span>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Admin Actions */}
                {(canEdit() || canDelete()) && (
                  <div className="flex items-center justify-end gap-2 pt-3 border-t border-gray-100 mt-3">
                    {canEdit() && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          handleEditInfographic(infographic)
                        }}
                        className="text-green-600 hover:text-green-800 transition-colors p-1"
                        title="Edit"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                    )}
                    {canDelete() && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          handleDeleteInfographic(infographic.id)
                        }}
                        className="text-red-600 hover:text-red-800 transition-colors p-1"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
        </>
      )}

      {/* Infographic Modal */}
      {showModal && selectedInfographic && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-6xl w-full max-h-[95vh] overflow-hidden">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div className="flex-1">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">{selectedInfographic.title}</h2>
                <div className="flex items-center gap-4 text-sm text-gray-600">
                  <span className="flex items-center">
                    <User className="w-4 h-4 mr-1" />
                    {selectedInfographic.authorName}
                  </span>
                  <span className="flex items-center">
                    <Calendar className="w-4 h-4 mr-1" />
                    {formatInfographicDate(selectedInfographic.publishedAt)}
                  </span>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                    {selectedInfographic.category}
                  </span>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    selectedInfographic.difficulty === 'beginner' ? 'bg-green-100 text-green-800' :
                    selectedInfographic.difficulty === 'intermediate' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {selectedInfographic.difficulty}
                  </span>
                </div>
              </div>
              <button
                onClick={closeModal}
                className="text-gray-400 hover:text-gray-600 p-2"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            {/* Modal Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(95vh-120px)]">
              {/* Image */}
              <div className="mb-6">
                {imageErrors[selectedInfographic.id] ? (
                  <div className="aspect-video bg-gray-100 rounded-lg flex items-center justify-center">
                    <div className="text-center">
                      <ImageIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-500">Image not available</p>
                    </div>
                  </div>
                ) : (
                  <img
                    src={selectedInfographic.imageUrl}
                    alt={selectedInfographic.altText || selectedInfographic.title}
                    className="w-full h-auto rounded-lg shadow-lg"
                    onError={() => handleImageError(selectedInfographic.id)}
                  />
                )}
              </div>
              
              {/* Description */}
              {selectedInfographic.description && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Description</h3>
                  <p className="text-gray-700 leading-relaxed">{selectedInfographic.description}</p>
                </div>
              )}
              
              {/* Stats */}
              <div className="grid grid-cols-3 gap-4 mb-6 p-4 bg-gray-50 rounded-lg">
                <div className="text-center">
                  <div className="flex items-center justify-center mb-2">
                    <Eye className="w-5 h-5 text-blue-600" />
                  </div>
                  <div className="text-2xl font-bold text-gray-900">{selectedInfographic.views || 0}</div>
                  <div className="text-sm text-gray-600">Views</div>
                </div>
                <div className="text-center">
                  <div className="flex items-center justify-center mb-2">
                    <Heart className="w-5 h-5 text-red-600" />
                  </div>
                  <div className="text-2xl font-bold text-gray-900">{selectedInfographic.likes || 0}</div>
                  <div className="text-sm text-gray-600">Likes</div>
                </div>
                <div className="text-center">
                  <div className="flex items-center justify-center mb-2">
                    <Download className="w-5 h-5 text-green-600" />
                  </div>
                  <div className="text-2xl font-bold text-gray-900">{selectedInfographic.downloads || 0}</div>
                  <div className="text-sm text-gray-600">Downloads</div>
                </div>
              </div>
              
              {/* Action Buttons */}
              <div className="flex items-center justify-between pt-6 border-t border-gray-200">
                <div className="flex gap-3">
                  <button
                    onClick={(e) => handleLikeToggle(selectedInfographic.id, e)}
                    className={`flex items-center px-4 py-2 rounded-lg transition-colors ${
                      likedInfographics.has(selectedInfographic.id)
                        ? 'bg-red-100 text-red-700 hover:bg-red-200'
                        : 'bg-gray-100 text-gray-700 hover:bg-red-100 hover:text-red-700'
                    }`}
                  >
                    <Heart className={`w-4 h-4 mr-2 ${likedInfographics.has(selectedInfographic.id) ? 'fill-current' : ''}`} />
                    {likedInfographics.has(selectedInfographic.id) ? 'Liked' : 'Like'}
                  </button>
                  
                  <button
                    onClick={(e) => handleDownload(selectedInfographic, e)}
                    className="flex items-center px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download
                  </button>
                </div>
                
                <button
                  onClick={closeModal}
                  className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Infographic Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Create New Infographic</h2>
                <button
                  onClick={closeCreateModal}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <form onSubmit={handleFormSubmit} className="space-y-6">
                {formErrors.submit && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                    {formErrors.submit}
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Title *
                    </label>
                    <input
                      type="text"
                      value={infographicForm.title}
                      onChange={(e) => setInfographicForm(prev => ({ ...prev, title: e.target.value }))}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        formErrors.title ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="Enter infographic title"
                    />
                    {formErrors.title && (
                      <p className="text-red-500 text-sm mt-1">{formErrors.title}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Category *
                    </label>
                    <select
                      value={infographicForm.category}
                      onChange={(e) => setInfographicForm(prev => ({ ...prev, category: e.target.value }))}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        formErrors.category ? 'border-red-300' : 'border-gray-300'
                      }`}
                    >
                      <option value="">Select category</option>
                      <option value="cybersecurity">Cybersecurity</option>
                      <option value="data-protection">Data Protection</option>
                      <option value="compliance">Compliance</option>
                      <option value="best-practices">Best Practices</option>
                      <option value="awareness">Awareness</option>
                    </select>
                    {formErrors.category && (
                      <p className="text-red-500 text-sm mt-1">{formErrors.category}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Difficulty
                    </label>
                    <select
                      value={infographicForm.difficulty}
                      onChange={(e) => setInfographicForm(prev => ({ ...prev, difficulty: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="beginner">Beginner</option>
                      <option value="intermediate">Intermediate</option>
                      <option value="advanced">Advanced</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Status
                    </label>
                    <select
                      value={infographicForm.status}
                      onChange={(e) => setInfographicForm(prev => ({ ...prev, status: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="draft">Draft</option>
                      <option value="published">Published</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description *
                  </label>
                  <textarea
                    value={infographicForm.description}
                    onChange={(e) => setInfographicForm(prev => ({ ...prev, description: e.target.value }))}
                    rows={4}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      formErrors.description ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="Enter infographic description"
                  />
                  {formErrors.description && (
                    <p className="text-red-500 text-sm mt-1">{formErrors.description}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Alt Text
                  </label>
                  <input
                    type="text"
                    value={infographicForm.altText}
                    onChange={(e) => setInfographicForm(prev => ({ ...prev, altText: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter alt text for accessibility"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tags
                  </label>
                  <input
                    type="text"
                    value={Array.isArray(infographicForm.tags) ? infographicForm.tags.join(', ') : infographicForm.tags}
                    onChange={(e) => setInfographicForm(prev => ({ ...prev, tags: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter tags separated by commas"
                  />
                </div>

                <div className="flex items-center space-x-4">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={infographicForm.featured}
                      onChange={(e) => setInfographicForm(prev => ({ ...prev, featured: e.target.checked }))}
                      className="mr-2"
                    />
                    Featured
                  </label>
                </div>

                {/* Image Upload Section */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Images * (Multiple images supported)
                  </label>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
                    <input
                      type="file"
                      multiple
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                      id="image-upload"
                    />
                    <label
                      htmlFor="image-upload"
                      className="cursor-pointer flex flex-col items-center justify-center"
                    >
                      <Upload className="w-12 h-12 text-gray-400 mb-4" />
                      <p className="text-gray-600 text-center">
                        Click to upload images or drag and drop
                      </p>
                      <p className="text-gray-500 text-sm mt-2">
                        PNG, JPG, GIF up to 10MB each
                      </p>
                    </label>
                  </div>
                  {formErrors.images && (
                    <p className="text-red-500 text-sm mt-1">{formErrors.images}</p>
                  )}

                  {/* Image Preview */}
                  {images.length > 0 && (
                    <div className="mt-4">
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Selected Images:</h4>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {images.map((image, index) => (
                          <div key={index} className="relative">
                            <img
                              src={URL.createObjectURL(image)}
                              alt={`Preview ${index + 1}`}
                              className="w-full h-24 object-cover rounded-lg"
                            />
                            <button
                              type="button"
                              onClick={() => handleRemoveImage(index)}
                              className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600"
                            >
                              ×
                            </button>
                            <p className="text-xs text-gray-500 mt-1 truncate">{image.name}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex justify-end space-x-4 pt-6 border-t">
                  <button
                    type="button"
                    onClick={closeCreateModal}
                    className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={uploadingImages}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50"
                  >
                    {uploadingImages ? 'Creating...' : 'Create Infographic'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit Infographic Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Edit Infographic</h2>
                <button
                  onClick={closeEditModal}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <form onSubmit={handleFormSubmit} className="space-y-6">
                {formErrors.submit && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                    {formErrors.submit}
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Title *
                    </label>
                    <input
                      type="text"
                      value={infographicForm.title}
                      onChange={(e) => setInfographicForm(prev => ({ ...prev, title: e.target.value }))}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        formErrors.title ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="Enter infographic title"
                    />
                    {formErrors.title && (
                      <p className="text-red-500 text-sm mt-1">{formErrors.title}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Category *
                    </label>
                    <select
                      value={infographicForm.category}
                      onChange={(e) => setInfographicForm(prev => ({ ...prev, category: e.target.value }))}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        formErrors.category ? 'border-red-300' : 'border-gray-300'
                      }`}
                    >
                      <option value="">Select category</option>
                      <option value="cybersecurity">Cybersecurity</option>
                      <option value="data-protection">Data Protection</option>
                      <option value="compliance">Compliance</option>
                      <option value="best-practices">Best Practices</option>
                      <option value="awareness">Awareness</option>
                    </select>
                    {formErrors.category && (
                      <p className="text-red-500 text-sm mt-1">{formErrors.category}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Difficulty
                    </label>
                    <select
                      value={infographicForm.difficulty}
                      onChange={(e) => setInfographicForm(prev => ({ ...prev, difficulty: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="beginner">Beginner</option>
                      <option value="intermediate">Intermediate</option>
                      <option value="advanced">Advanced</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Status
                    </label>
                    <select
                      value={infographicForm.status}
                      onChange={(e) => setInfographicForm(prev => ({ ...prev, status: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="draft">Draft</option>
                      <option value="published">Published</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description *
                  </label>
                  <textarea
                    value={infographicForm.description}
                    onChange={(e) => setInfographicForm(prev => ({ ...prev, description: e.target.value }))}
                    rows={4}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      formErrors.description ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="Enter infographic description"
                  />
                  {formErrors.description && (
                    <p className="text-red-500 text-sm mt-1">{formErrors.description}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Alt Text
                  </label>
                  <input
                    type="text"
                    value={infographicForm.altText}
                    onChange={(e) => setInfographicForm(prev => ({ ...prev, altText: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter alt text for accessibility"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tags
                  </label>
                  <input
                    type="text"
                    value={Array.isArray(infographicForm.tags) ? infographicForm.tags.join(', ') : infographicForm.tags}
                    onChange={(e) => setInfographicForm(prev => ({ ...prev, tags: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter tags separated by commas"
                  />
                </div>

                <div className="flex items-center space-x-4">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={infographicForm.featured}
                      onChange={(e) => setInfographicForm(prev => ({ ...prev, featured: e.target.checked }))}
                      className="mr-2"
                    />
                    Featured
                  </label>
                </div>

                {/* Additional Image Upload Section for Edit */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Add More Images (Optional)
                  </label>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
                    <input
                      type="file"
                      multiple
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                      id="edit-image-upload"
                    />
                    <label
                      htmlFor="edit-image-upload"
                      className="cursor-pointer flex flex-col items-center justify-center"
                    >
                      <Upload className="w-12 h-12 text-gray-400 mb-4" />
                      <p className="text-gray-600 text-center">
                        Click to upload additional images
                      </p>
                      <p className="text-gray-500 text-sm mt-2">
                        PNG, JPG, GIF up to 10MB each
                      </p>
                    </label>
                  </div>

                  {/* New Image Preview */}
                  {images.length > 0 && (
                    <div className="mt-4">
                      <h4 className="text-sm font-medium text-gray-700 mb-2">New Images to Upload:</h4>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {images.map((image, index) => (
                          <div key={index} className="relative">
                            <img
                              src={URL.createObjectURL(image)}
                              alt={`Preview ${index + 1}`}
                              className="w-full h-24 object-cover rounded-lg"
                            />
                            <button
                              type="button"
                              onClick={() => handleRemoveImage(index)}
                              className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600"
                            >
                              ×
                            </button>
                            <p className="text-xs text-gray-500 mt-1 truncate">{image.name}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex justify-end space-x-4 pt-6 border-t">
                  <button
                    type="button"
                    onClick={closeEditModal}
                    className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={uploadingImages}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50"
                  >
                    {uploadingImages ? 'Updating...' : 'Update Infographic'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Infographics